import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { toggleFormatError } from '../../api/client';

// Color schemes for different formats
const FORMAT_COLORS = {
  HLS: { light: '#9c27b0', dark: '#ce93d8' },  // Purple for HLS
  DASH: { light: '#ff9800', dark: '#ffb74d' }, // Orange for DASH
};

function FormatErrorCard({ pipeline, format, isActive, onToggle }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);

  const colors = FORMAT_COLORS[format.toUpperCase()] || FORMAT_COLORS.HLS;
  const colorLight = colors.light;
  const colorDark = colors.dark;
  const activeColor = isDark ? colorDark : colorLight;

  const handleClick = async () => {
    try {
      setLoading(true);
      await toggleFormatError(pipeline.id, format);
      if (onToggle) {
        await onToggle();
      }
    } catch (error) {
      console.error(`Failed to toggle ${format} error:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Convert hex to rgba for backgrounds
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <Tooltip
      title={
        isActive
          ? `Click to restore ${format} streams`
          : `Click to simulate error on all ${format} streams`
      }
    >
      <Card
        sx={{
          background: isActive
            ? hexToRgba(colorLight, isDark ? 0.2 : 0.1)
            : isDark
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(0, 0, 0, 0.02)',
          border: isActive
            ? `2px solid ${activeColor}`
            : `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isActive
              ? `0 4px 12px ${hexToRgba(colorLight, 0.3)}`
              : `0 4px 12px ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <CardActionArea onClick={handleClick} disabled={loading}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 1.5,
              px: 1,
              minHeight: 80,
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: colorLight }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <VideoFileIcon
                  sx={{
                    fontSize: 28,
                    color: isActive
                      ? activeColor
                      : isDark
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.26)',
                    animation: isActive ? 'pulse 1.5s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isActive ? activeColor : 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {format} Error
                </Typography>
                {isActive && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      color: activeColor,
                      opacity: 0.8,
                    }}
                  >
                    ACTIVE
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
}

export default FormatErrorCard;
